import { Component, Input, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ConsentManager, ConsentTarget } from '../../_services/consent/consent-manager.service';

/** Domaines autorisés par service : on ne charge jamais une iframe hors de cette liste. */
const ALLOWED_HOSTS: Record<ConsentTarget, string[]> = {
  maps: ['maps.google.com', 'www.google.com'],
  youtube: ['www.youtube-nocookie.com', 'www.youtube.com'],
};

const LABELS: Record<ConsentTarget, { nom: string; editeur: string }> = {
  maps: { nom: 'la carte Google Maps', editeur: 'Google' },
  youtube: { nom: 'la vidéo YouTube', editeur: 'YouTube (Google)' },
};

/**
 * Affiche un contenu tiers (carte, vidéo) uniquement après consentement.
 * Tant que le visiteur n'a pas accepté, AUCUNE requête n'est envoyée au service tiers :
 * on affiche un encart explicatif à la place de l'iframe.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-consent-embed',
  template: `
    @if (allowed() && safeSrc) {
      <iframe
        class="h-full w-full"
        [src]="safeSrc"
        [title]="title"
        allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        referrerpolicy="strict-origin-when-cross-origin"
        loading="lazy"
        allowfullscreen></iframe>
    } @else {
      <div class="flex h-full w-full flex-col items-center justify-center gap-3 bg-gray-100 p-4 text-center text-gray-800">
        <p class="font-raleway text-sm sm:text-base">
          Pour afficher {{ label.nom }}, votre accord est nécessaire : ce contenu est fourni par {{ label.editeur }},
          qui peut déposer des cookies et recevoir votre adresse IP.
        </p>
        <div class="flex flex-wrap items-center justify-center gap-2">
          <button type="button" (click)="consent.allow(service)"
            class="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-secondary">
            Afficher {{ label.nom }}
          </button>
          @if (fallbackUrl) {
            <a [href]="fallbackUrl" target="_blank" rel="noopener noreferrer"
              class="rounded-md border border-gray-500 bg-white px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50">
              {{ fallbackLabel }}<span class="sr-only"> (nouvel onglet)</span>
            </a>
          }
        </div>
        <p class="text-xs text-gray-700">
          <button type="button" (click)="consent.openPreferences()" class="underline">Gérer mes cookies</button>
          ·
          <a href="/cookies" class="underline">Politique de cookies</a>
        </p>
      </div>
    }
  `,
  styles: [`:host { display: block; height: 100%; width: 100%; }`],
})
export class ConsentEmbedComponent {
  consent = inject(ConsentManager);
  private sanitizer = inject(DomSanitizer);

  @Input({ required: true }) service!: ConsentTarget;
  @Input({ required: true }) title!: string;
  /** Lien alternatif qui ne charge rien chez un tiers tant que la personne n'a pas cliqué. */
  @Input() fallbackUrl = '';
  @Input() fallbackLabel = 'Ouvrir dans un nouvel onglet';

  private _src = '';
  safeSrc: SafeResourceUrl | null = null;

  @Input({ required: true })
  set src(value: string) {
    this._src = value;
    this.safeSrc = this.isTrusted(value) ? this.sanitizer.bypassSecurityTrustResourceUrl(value) : null;
  }
  get src(): string {
    return this._src;
  }

  allowed = computed(() => this.consent.ready() && this.consent.choices()[this.service]);

  get label() {
    return LABELS[this.service];
  }

  private isTrusted(value: string): boolean {
    try {
      const url = new URL(value);
      return url.protocol === 'https:' && ALLOWED_HOSTS[this.service].includes(url.hostname);
    } catch {
      return false;
    }
  }
}
